/**
 * Coups de pouce — Attendu 1048 « écriture décimale des fractions simples »
 * (automatisme 5e n°10).
 *
 * Carte 1 (usuelles_*) : DÉLÉGUÉE à gen_1012, son coup de pouce est repris
 * tel quel (le carré des centièmes, c'est une moitié).
 *
 * Carte 2 (entiers_*) : une page, le SENS QUOTIENT (6e) qui unifie tout,
 * en TROIS BLOCS de même poids (revue prof 2026-09-07), chacun portant sa
 * règle en en-tête et son exemple, aucune phrase hors des blocs :
 *   - bleu « Le numérateur divisé par le dénominateur » : 12/1 = 12 ÷ 1 = 12,
 *     12/12 = 12 ÷ 12 = 1, 120/10 = 120 ÷ 10 = 12 ;
 *   - orange « Sur 2, c'est une moitié » : 17/2, c'est la moitié de 17 : 8,5
 *     (le fait s'est construit au CM2 comme moitié d'un impair, un geste,
 *     pas une décomposition en 8 + 1/2) ;
 *   - vert « Sur 10 ou sur 100, je lis le rang » : 3/10, trois dixièmes :
 *     0,3 ; 12/100, douze centièmes : 0,12.
 * EXEMPLES SACRIFIÉS, hors domaine par construction (n/1 avec n ≤ 9, k/k
 * avec k ≤ 10 ou 100, 10n/10 avec n ≤ 9, n/2 avec n ≤ 15) ou exclus des
 * numérateurs (3 pour les dixièmes, 12 pour les centièmes).
 * Invariant protégé par src/generators/tests/cdp1048.self.test.js.
 *
 * Le texte vit dans la figure (même parti que 1046 et 1047).
 */
import cp1012 from './1012';

const ENCRE = '#1E293B';
const BLEU = '#2563EB';
const BLEU_FOND = '#EFF6FF';
const ORANGE = '#EA580C';
const ORANGE_FOND = '#FFF7ED';
const VERT = '#15803D';
const VERT_FOND = '#F0FDF4';

/** Une fraction empilée en SVG, centrée en (x, y). */
const frac = (x, y, n, d, couleur = ENCRE) => `
  <text x="${x}" y="${y - 6}" font-size="12.5" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - 15}" y1="${y}" x2="${x + 15}" y2="${y}" stroke="${couleur}" stroke-width="2"/>
  <text x="${x}" y="${y + 19}" font-size="12.5" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

const bloc = (y, h, fond, bord, titre) => `
  <rect x="14" y="${y}" width="372" height="${h}" rx="10" fill="${fond}" stroke="${bord}" stroke-width="1.5"/>
  <text x="200" y="${y + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${bord}">${titre}</text>`;

const SVG_QUOTIENT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 424" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="Une fraction, c'est une division. Le numérateur divisé par le dénominateur : 12 sur 1, c'est 12 divisé par 1, égale 12 ; 12 sur 12, c'est 12 divisé par 12, égale 1 ; 120 sur 10, c'est 120 divisé par 10, égale 12. Sur 2, c'est une moitié : 17 sur 2, c'est la moitié de 17 : 8,5. Sur 10 ou sur 100, je lis le rang : 3 sur 10, c'est trois dixièmes : 0,3 ; 12 sur 100, c'est douze centièmes : 0,12.">
  ${bloc(14, 176, BLEU_FOND, BLEU, 'Le numérateur divisé par le dénominateur')}
  ${frac(70, 62, 12, 1, BLEU)}
  <text x="160" y="68" font-size="12.5" text-anchor="middle" fill="${ENCRE}">= 12 ÷ 1 =</text>
  <text x="240" y="68" font-size="14" font-weight="bold" text-anchor="middle" fill="${BLEU}">12</text>
  ${frac(70, 110, 12, 12, BLEU)}
  <text x="160" y="116" font-size="12.5" text-anchor="middle" fill="${ENCRE}">= 12 ÷ 12 =</text>
  <text x="240" y="116" font-size="14" font-weight="bold" text-anchor="middle" fill="${BLEU}">1</text>
  ${frac(70, 158, 120, 10, BLEU)}
  <text x="160" y="164" font-size="12.5" text-anchor="middle" fill="${ENCRE}">= 120 ÷ 10 =</text>
  <text x="240" y="164" font-size="14" font-weight="bold" text-anchor="middle" fill="${BLEU}">12</text>
  ${bloc(202, 78, ORANGE_FOND, ORANGE, 'Sur 2, c\'est une moitié')}
  ${frac(70, 250, 17, 2, ORANGE)}
  <text x="230" y="256" font-size="12" text-anchor="middle" fill="${ENCRE}">, c'est la moitié de 17 : <tspan font-size="14" font-weight="bold" fill="${ORANGE}">8,5</tspan></text>
  ${bloc(292, 118, VERT_FOND, VERT, 'Sur 10 ou sur 100, je lis le rang')}
  ${frac(70, 340, 3, 10, VERT)}
  <text x="230" y="346" font-size="12" text-anchor="middle" fill="${ENCRE}">, c'est trois dixièmes : <tspan font-size="14" font-weight="bold" fill="${VERT}">0,3</tspan></text>
  ${frac(70, 378, 12, 100, VERT)}
  <text x="230" y="384" font-size="12" text-anchor="middle" fill="${ENCRE}">, c'est douze centièmes : <tspan font-size="14" font-weight="bold" fill="${VERT}">0,12</tspan></text>
</svg>`.trim();

const AIDE_ENTIERS = {
    pages: [
        { titre: 'Une fraction, c\'est une division', svg: SVG_QUOTIENT },
    ],
};

const CP_1048 = {
    ...cp1012,
    entiers_texte_trou_saisie: AIDE_ENTIERS,
    entiers_qcm_unique: AIDE_ENTIERS,
    entiers_tableau_vf: AIDE_ENTIERS,
};

export default CP_1048;
